import { useEffect, type RefObject } from 'react';

const HEADER_SELECTORS = [
  '[class*="ModalPageHeader"]',
  '[class*="ModalPage__header"]',
  '[class*="modal-page__header"]',
  '[data-sheet-drag-handle]',
];

/** Whole bottom-sheet panel (VKUI: ModalPage__document). */
const SHEET_SELECTORS = [
  '[class*="ModalPage__document"]',
  '[class*="ModalPageDocument"]',
  '[class*="modal-page__document"]',
];

function findSheetEl(from: HTMLElement | null): HTMLElement | null {
  if (!from) return null;
  for (const sel of SHEET_SELECTORS) {
    const el = from.closest(sel) as HTMLElement | null;
    if (el) return el;
  }
  // climb up and pick the tallest known modal container
  let node: HTMLElement | null = from;
  for (let i = 0; i < 8 && node; i++) {
    if (node && SHEET_SELECTORS.some((s) => node!.matches(s))) return node;
    node = node.parentElement;
  }
  return (
    (from.closest('[class*="ModalPage"]') as HTMLElement | null) ||
    from
  );
}

/**
 * Immediate pointer-drag on the sheet header (no long-press).
 * Moves the whole sheet (`ModalPage__document`), not just the title.
 * Drag down far/fast → onClose.
 */
export function useSheetHeaderDrag(
  containerRef: RefObject<HTMLElement | null>,
  opts: {
    active: boolean;
    onClose?: () => void;
    onExpand?: () => void;
  },
) {
  const { active, onClose, onExpand } = opts;

  useEffect(() => {
    if (!active) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;
    const timers: number[] = [];

    const bind = () => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!container) return;

      const zones = new Set<HTMLElement>();
      HEADER_SELECTORS.forEach((sel) => {
        container.querySelectorAll(sel).forEach((el) => zones.add(el as HTMLElement));
      });
      if (zones.size === 0) return;

      let startY = 0;
      let lastY = 0;
      let pointerId = -1;
      let sheet: HTMLElement | null = null;
      let startTime = 0;
      let dragging = false;

      const clearTransform = () => {
        if (sheet) {
          sheet.style.removeProperty('transform');
          sheet.style.removeProperty('transition');
        }
      };

      const reset = () => {
        clearTransform();
        sheet = null;
        dragging = false;
        pointerId = -1;
      };

      const onPointerDown = (e: PointerEvent) => {
        if (e.button != null && e.button !== 0) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, a, input, select, textarea, [role="button"]')) return;

        // stop VKUI Touch long-press pan from fighting us
        e.stopPropagation();

        sheet = findSheetEl(e.currentTarget as HTMLElement);
        if (sheet) {
          sheet.style.setProperty('transition', 'none');
          sheet.style.setProperty('will-change', 'transform');
        }
        startY = e.clientY;
        lastY = e.clientY;
        pointerId = e.pointerId;
        startTime = Date.now();
        dragging = true;
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        e.preventDefault();
      };

      const onPointerMove = (e: PointerEvent) => {
        if (!dragging || e.pointerId !== pointerId || !sheet) return;
        e.stopPropagation();
        lastY = e.clientY;
        const dy = e.clientY - startY;
        const y = dy > 0 ? dy : dy * 0.3;
        sheet.style.setProperty('transform', `translate3d(0, ${y}px, 0)`);
        e.preventDefault();
      };

      const finish = (e: PointerEvent | null) => {
        if (!dragging) return;
        const dy = e ? e.clientY - startY : lastY - startY;
        const dt = Date.now() - startTime;
        const velocity = dy / Math.max(dt, 1);
        const el = sheet;
        reset();
        if (!el) return;

        if (dy > 110 || velocity > 0.55) {
          onClose?.();
          return;
        }
        if (dy < -70) {
          onExpand?.();
        }
      };

      const onPointerUp = (e: PointerEvent) => {
        e.stopPropagation();
        finish(e);
      };
      const onPointerCancel = () => finish(null);

      zones.forEach((z) => {
        z.style.setProperty('touch-action', 'none');
        z.style.setProperty('cursor', 'grab');
        z.style.setProperty('user-select', 'none');
        z.addEventListener('pointerdown', onPointerDown, { passive: false });
        z.addEventListener('pointermove', onPointerMove, { passive: false });
        z.addEventListener('pointerup', onPointerUp);
        z.addEventListener('pointercancel', onPointerCancel);
      });

      return () => {
        zones.forEach((z) => {
          z.removeEventListener('pointerdown', onPointerDown);
          z.removeEventListener('pointermove', onPointerMove);
          z.removeEventListener('pointerup', onPointerUp);
          z.removeEventListener('pointercancel', onPointerCancel);
        });
      };
    };

    const raf = requestAnimationFrame(() => {
      cleanup = bind();
      timers.push(
        window.setTimeout(() => {
          cleanup?.();
          cleanup = bind();
        }, 40),
      );
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
      cleanup?.();
    };
  }, [active, containerRef, onClose, onExpand]);
}
